/**
 * v3.18-1260 PAYMENT-005 — Shared Receipt Viewer.
 *
 * SINGLE receipt UI component used from every entry point (Payment
 * Center Finance History, Client Activity Timeline, Purchase Proposal
 * — anywhere payment information appears).
 *
 * Behavior:
 *   • If the transaction has a ``provider_receipt_url`` (hosted
 *     receipt from the payment provider), that URL is opened in a
 *     new tab and no dialog is shown.
 *   • Otherwise a Hub receipt dialog is rendered with the canonical
 *     transaction details.
 *
 * Data source: the ONE ``payment_transactions`` collection, written
 * only by ``services.payment_engine``. This component reads it via
 * ``GET /api/payments/engine/transactions/{id}`` and never persists
 * anything back.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Printer, Download, Mail, MessageSquare, X, ExternalLink, Receipt } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const fmtMoney = (amt, ccy) => {
  const n = typeof amt === 'string' ? parseFloat(amt) : amt;
  if (n == null || Number.isNaN(n)) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: (ccy || 'USD').toUpperCase() }).format(n); }
  catch { return `${(ccy || 'USD').toUpperCase()} ${n.toFixed(2)}`; }
};
const fmtDate = (iso) => { if (!iso) return { date: '—', time: '' }; try { const d = new Date(iso); return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() }; } catch { return { date: iso, time: '' }; } };

/** Build a self-contained printable HTML document for the receipt. */
const buildPrintableHtml = (tx) => {
  const { date, time } = fmtDate(tx.updated_at || tx.created_at);
  const statusClass = (tx.status || '').toLowerCase();
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Payment Receipt — ${tx.hub_reference || tx.id || ''}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; margin: 0; padding: 40px; color: #0f172a; background: #f8fafc; }
    .receipt { max-width: 640px; margin: 0 auto; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 32px; }
    h1 { font-size: 22px; margin: 0 0 4px 0; }
    .sub { color: #64748b; font-size: 13px; margin-bottom: 24px; }
    .amount { font-size: 34px; font-weight: 700; margin: 8px 0 24px 0; }
    .status { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
    .status.approved { background: #dcfce7; color: #166534; }
    .status.declined, .status.error { background: #fee2e2; color: #991b1b; }
    .status.initiated { background: #fef3c7; color: #92400e; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    td { padding: 8px 4px; font-size: 13px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
    td.label { color: #64748b; width: 42%; }
    td.value { color: #0f172a; font-weight: 500; }
    .mono { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; }
    .footer { margin-top: 24px; font-size: 11px; color: #94a3b8; text-align: center; }
    @media print { body { padding: 0; background: #fff; } .receipt { border: 0; box-shadow: none; } }
  </style>
</head>
<body>
  <div class="receipt">
    <h1>Payment Receipt</h1>
    <p class="sub">Prestigious Hub · ${tx.hub_reference || ''}</p>
    <span class="status ${statusClass}">${tx.status || 'unknown'}</span>
    <div class="amount">${fmtMoney(tx.amount, tx.currency)}</div>
    <table>
      <tr><td class="label">Client</td><td class="value">${tx.client_name || '—'}</td></tr>
      ${tx.client_email ? `<tr><td class="label">Email</td><td class="value">${tx.client_email}</td></tr>` : ''}
      <tr><td class="label">Deal</td><td class="value">${tx.deal_name || tx.deal_id || '—'}</td></tr>
      <tr><td class="label">Date</td><td class="value">${date}</td></tr>
      <tr><td class="label">Time</td><td class="value">${time}</td></tr>
      <tr><td class="label">Authorization Number</td><td class="value mono">${tx.provider_authorization_number || '—'}</td></tr>
      <tr><td class="label">Transaction ID</td><td class="value mono">${tx.provider_transaction_id || '—'}</td></tr>
      <tr><td class="label">Hub Reference</td><td class="value mono">${tx.hub_reference || tx.id || '—'}</td></tr>
      <tr><td class="label">Terminal</td><td class="value">${tx.terminal_label || '—'}</td></tr>
      <tr><td class="label">Operator</td><td class="value">${tx.operator_email || '—'}</td></tr>
      ${tx.memo ? `<tr><td class="label">Memo</td><td class="value">${tx.memo}</td></tr>` : ''}
    </table>
    <p class="footer">Retain this receipt for your records.</p>
  </div>
</body>
</html>`;
};

export const ReceiptViewer = ({ open, onOpenChange, transactionId, transaction: providedTx }) => {
  const [tx, setTx] = useState(providedTx || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openedHosted, setOpenedHosted] = useState(false);

  const load = useCallback(async () => {
    if (providedTx) { setTx(providedTx); return; }
    if (!transactionId) return;
    setLoading(true); setError('');
    try {
      const res = await api.get(`/payments/engine/transactions/${transactionId}`);
      setTx(res.data);
    } catch (e) {
      const m = e?.response?.data?.detail || 'Failed to load receipt';
      setError(typeof m === 'string' ? m : 'Failed to load receipt');
    } finally { setLoading(false); }
  }, [providedTx, transactionId]);

  useEffect(() => {
    if (!open) { setOpenedHosted(false); return; }
    load();
  }, [open, load]);

  // If a hosted receipt exists, open it and close the dialog.
  useEffect(() => {
    if (!open || !tx || openedHosted) return;
    if (tx.provider_receipt_url) {
      window.open(tx.provider_receipt_url, '_blank', 'noopener,noreferrer');
      setOpenedHosted(true);
      toast.success('Opened hosted receipt in a new tab.');
      onOpenChange(false);
    }
  }, [open, tx, openedHosted, onOpenChange]);

  const { date, time } = useMemo(
    () => fmtDate(tx?.updated_at || tx?.created_at),
    [tx],
  );

  const doPrint = () => {
    if (!tx) return;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=720,height=900');
    if (!win) { toast.error('Popup blocked. Please allow popups to print.'); return; }
    win.document.write(buildPrintableHtml(tx));
    win.document.close();
    // Give the print window a beat to lay out.
    setTimeout(() => { try { win.focus(); win.print(); } catch (_e) { /* print cancelled/blocked */ } }, 250);
  };

  const doDownload = () => {
    if (!tx) return;
    const html = buildPrintableHtml(tx);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${(tx.hub_reference || tx.id || 'payment').replace(/[^A-Za-z0-9_-]/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
  };

  const statusMeta = useMemo(() => {
    const s = (tx?.status || '').toLowerCase();
    if (s === 'approved') return { cls: 'border-emerald-300 text-emerald-700 bg-emerald-50', label: 'Approved' };
    if (s === 'declined') return { cls: 'border-red-300 text-red-700 bg-red-50', label: 'Declined' };
    if (s === 'error') return { cls: 'border-red-300 text-red-700 bg-red-50', label: 'Error' };
    if (s === 'initiated') return { cls: 'border-amber-300 text-amber-700 bg-amber-50', label: 'Initiated' };
    return { cls: 'border-slate-300 text-slate-600 bg-slate-50', label: tx?.status || 'Unknown' };
  }, [tx]);

  return (
    <Dialog open={open && !openedHosted} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="receipt-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />Payment Receipt
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8">
            <Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading receipt…</span>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        ) : tx ? (
          <div className="space-y-4" data-testid="receipt-viewer-body">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-slate-500">Amount</p>
                <p className="text-3xl font-semibold text-slate-900" data-testid="receipt-amount">{fmtMoney(tx.amount, tx.currency)}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusMeta.cls}`} data-testid="receipt-status">
                {statusMeta.label}
              </Badge>
            </div>
            <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 text-sm">
              <ReceiptRow label="Client" value={tx.client_name} testId="receipt-client" />
              {tx.client_email ? <ReceiptRow label="Email" value={tx.client_email} /> : null}
              <ReceiptRow label="Deal" value={tx.deal_name || tx.deal_id || '—'} testId="receipt-deal" />
              <ReceiptRow label="Date" value={date} testId="receipt-date" />
              <ReceiptRow label="Time" value={time} testId="receipt-time" />
              <ReceiptRow label="Authorization #" value={tx.provider_authorization_number || '—'} mono testId="receipt-auth" />
              <ReceiptRow label="Transaction ID" value={tx.provider_transaction_id || '—'} mono testId="receipt-txn" />
              <ReceiptRow label="Hub Reference" value={tx.hub_reference || tx.id} mono testId="receipt-hubref" />
              <ReceiptRow label="Terminal" value={tx.terminal_label || '—'} testId="receipt-terminal" />
              <ReceiptRow label="Operator" value={tx.operator_email || '—'} testId="receipt-operator" />
              {tx.memo ? <ReceiptRow label="Memo" value={tx.memo} /> : null}
            </div>
            {tx.provider_receipt_url ? (
              <a href={tx.provider_receipt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 text-xs" data-testid="receipt-hosted-link">
                <ExternalLink className="h-3.5 w-3.5" />Open hosted receipt
              </a>
            ) : null}
          </div>
        ) : null}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled title="Coming soon — email delivery is not enabled in this phase" data-testid="receipt-email-btn">
            <Mail className="h-4 w-4 mr-1.5" />Email Receipt
          </Button>
          <Button variant="outline" size="sm" disabled title="Coming soon — SMS delivery is not enabled in this phase" data-testid="receipt-sms-btn">
            <MessageSquare className="h-4 w-4 mr-1.5" />SMS Receipt
          </Button>
          <Button variant="outline" size="sm" onClick={doDownload} disabled={!tx || loading} data-testid="receipt-download-btn">
            <Download className="h-4 w-4 mr-1.5" />Download
          </Button>
          <Button variant="outline" size="sm" onClick={doPrint} disabled={!tx || loading} data-testid="receipt-print-btn">
            <Printer className="h-4 w-4 mr-1.5" />Print
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)} data-testid="receipt-close-btn">
            <X className="h-4 w-4 mr-1.5" />Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReceiptRow = ({ label, value, mono, testId }) => (
  <div className="flex items-start justify-between gap-4 px-3 py-2">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm ${mono ? 'font-mono text-xs' : ''} text-slate-900 text-right break-all`} data-testid={testId}>{value ?? '—'}</span>
  </div>
);

export default ReceiptViewer;
